import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, formatCurrency } from "@/components/shared";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  number: "", lot_type: "RV Site", size: "Regular", pad_size: "",
  monthly_rent: 0, premium_fee: 0, active: true,
  has_electric: true, has_water: true, has_sewer: true,
  has_cable: false, has_wifi: true, has_bbq: false,
  notes: "", row: 0, col: 0,
};

export default function LotsPage() {
  const { user } = useAuth();
  const canManage = ["admin", "super_admin", "park_admin"].includes(user?.role);
  const { currentPark } = usePark();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lots");
      setLots(data);
    } catch { toast.error("Failed to load lots"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (currentPark?.id) load(); }, [currentPark?.id]);

  const openNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (lot) => {
    setEditingId(lot.id);
    const { id, created_at, ...rest } = lot;
    setForm({ ...empty, ...rest });
    setOpen(true);
  };

  const save = async () => {
    if (!form.number) { toast.error("Lot number is required"); return; }
    try {
      if (editingId) {
        await api.put(`/lots/${editingId}`, form);
        toast.success("Lot updated");
      } else {
        await api.post("/lots", form);
        toast.success("Lot created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (lot) => {
    if (!window.confirm(`Delete lot #${lot.number}?`)) return;
    try {
      await api.delete(`/lots/${lot.id}`);
      toast.success("Lot deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="lots-page">
      <PageHeader
        title="Lots & Sites"
        subtitle={`${lots.length} lot${lots.length === 1 ? "" : "s"} in inventory`}
        actions={canManage && (
          <Button onClick={openNew} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="new-lot-button"><Plus size={14} className="mr-2" /> New Lot</Button>
        )}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!bg-white border-[#EAEAE5] max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{editingId ? "Edit Lot" : "New Lot"}</DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">{editingId ? "Update lot details, utilities and position." : "Add a new lot or site to the park."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Number</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="lot-number-input" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#737A76]">Type</Label>
                <select
                  value={form.lot_type}
                  onChange={(e) => setForm({ ...form, lot_type: e.target.value })}
                  className="w-full h-10 border border-[#EAEAE5] rounded-md px-3 bg-white text-sm"
                  data-testid="lot-type-select"
                >
                  <option value="RV Site">RV Site</option>
                  <option value="Park Model (Park)">Park Model (Park)</option>
                  <option value="Park Model (Owner)">Park Model (Owner)</option>
                  <option value="Mobile Home (Park)">Mobile Home (Park)</option>
                  <option value="Mobile Home (Owner)">Mobile Home (Owner)</option>
                  <option value="Dumpster">Dumpster</option>
                  <option value="Storage">Storage</option>
                </select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Size</Label><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Pad Size</Label><Input value={form.pad_size} onChange={(e) => setForm({ ...form, pad_size: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Rent</Label><Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Premium fee</Label><Input type="number" value={form.premium_fee} onChange={(e) => setForm({ ...form, premium_fee: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Row</Label><Input type="number" value={form.row} onChange={(e) => setForm({ ...form, row: parseInt(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Column</Label><Input type="number" value={form.col} onChange={(e) => setForm({ ...form, col: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="flex items-center gap-2 pt-1"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="lot-active-switch" /><span className="text-sm">Active</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
              {[["has_electric","Electric"],["has_water","Water"],["has_sewer","Sewer"],["has_cable","Cable"],["has_wifi","WiFi"],["has_bbq","BBQ"]].map(([k, l]) => (
                <div key={k} className="flex items-center gap-2"><Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} /><span className="text-sm">{l}</span></div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={save} data-testid="save-lot-submit">{editingId ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden hidden md:block">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm pd-table">
            <thead>
              <tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
                <th className="px-4 py-3">Lot #</th><th>Type</th><th>Size</th><th>Pad</th><th>Rent</th><th>Premium</th><th>Utilities</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={9} className="px-4 py-8 text-center text-[#737A76]">Loading…</td></tr>
              : lots.length === 0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-[#737A76]">No lots yet.</td></tr>
              : lots.map((l) => (
                <tr key={l.id} className="border-b border-[#F0F0EC] last:border-0" data-testid={`lot-row-${l.id}`}>
                  <td className="px-4 py-3 font-medium">#{l.number}</td>
                  <td>{l.lot_type}</td>
                  <td>{l.size}</td>
                  <td className="text-[#737A76]">{l.pad_size || "—"}</td>
                  <td>{formatCurrency(l.monthly_rent)}</td>
                  <td className="text-[#737A76]">{l.premium_fee > 0 ? formatCurrency(l.premium_fee) : "—"}</td>
                  <td className="text-[10px] uppercase tracking-wider text-[#737A76]">
                    {[l.has_electric && "Elec", l.has_water && "Water", l.has_sewer && "Sewer", l.has_wifi && "WiFi", l.has_cable && "Cable", l.has_bbq && "BBQ"].filter(Boolean).join(" · ")}
                  </td>
                  <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${l.active ? "bg-[#E3F0E6] text-[#297343]" : "bg-[#F0F0EC] text-[#737A76]"}`}>{l.active ? "Active" : "Inactive"}</span></td>
                  <td className="text-right pr-4">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-lot-${l.id}`}><Pencil size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(l)} data-testid={`delete-lot-${l.id}`}><Trash2 size={14} className="text-[#A13E34]" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Mobile card list (≤ md) */}
      <div className="md:hidden space-y-2" data-testid="lots-mobile-list">
        {loading ? (
          <div className="bg-white border border-[#EAEAE5] rounded-lg p-6 text-center text-[#737A76] text-sm">Loading…</div>
        ) : lots.length === 0 ? (
          <div className="bg-white border border-[#EAEAE5] rounded-lg p-6 text-center text-[#737A76] text-sm">No lots yet.</div>
        ) : lots.map((l) => (
          <div key={l.id} className="bg-white border border-[#EAEAE5] rounded-lg p-3" data-testid={`lot-card-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono tabular-nums font-semibold text-[#1A211D]">#{l.number}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${l.active ? "bg-[#E3F0E6] text-[#297343]" : "bg-[#F0F0EC] text-[#737A76]"}`}>
                    {l.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="text-xs text-[#737A76] mt-1">{l.lot_type}{l.size ? <> · {l.size}</> : null}{l.pad_size ? <> · pad {l.pad_size}</> : null}</div>
                <div className="text-xs text-[#1A211D] mt-1 tabular-nums">
                  <span className="font-medium">{formatCurrency(l.monthly_rent)}</span>
                  {l.premium_fee > 0 && <span className="text-[#737A76]"> + {formatCurrency(l.premium_fee)} premium</span>}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#737A76] mt-1.5">
                  {[l.has_electric && "Elec", l.has_water && "Water", l.has_sewer && "Sewer", l.has_wifi && "WiFi", l.has_cable && "Cable", l.has_bbq && "BBQ"].filter(Boolean).join(" · ") || "No utilities"}
                </div>
              </div>
              {canManage && (
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(l)} className="h-10 w-10" data-testid={`edit-lot-mobile-${l.id}`}><Pencil size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(l)} className="h-10 w-10" data-testid={`delete-lot-mobile-${l.id}`}><Trash2 size={16} className="text-[#A13E34]" /></Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
